import { useCallback, useEffect, useState } from "react";
import { api, mesajEroare } from "./api.js";
import { data } from "./text.js";
import { tr } from "./i18n.js";

/**
 * §39 — jurisdicția firmei: în ce monedă se scriu banii, cu ce cotă se
 * taxează, pe ce ceas curge un termen.
 *
 * Ecranul are o singură treabă importantă: să spună CE SE APLICĂ ACUM și de
 * unde vine. Ordinea (pachetul obiectivului bate alegerea firmei) e ușor de
 * pus greșit în cap, iar un refuz de calcul care apare peste trei ecrane, în
 * mijlocul unui deviz, e cel mai prost loc în care să afli că nu e nimic
 * configurat.
 */

const NUME_SURSA = {
  pachet: "pachetul de țară instalat",
  firma: "alegerile firmei",
  nimic: "nimic — calculele cu bani vor refuza",
};

export default function Jurisdictie({ eu }) {
  const [stare, setStare] = useState(null);
  const [incarca, setIncarca] = useState(true);
  const [eroare, setEroare] = useState("");
  const [mesaj, setMesaj] = useState("");
  const [lucreaza, setLucreaza] = useState("");
  const [form, setForm] = useState({ moneda: "", cotaTVAImplicita: "", fusOrar: "", taraStabilire: "" });

  // Butonul se citește din MATRICE, nu dintr-o listă de roluri scrisă aici.
  // Dacă cererea pică, rămâne valoarea de pornire — un ecran care nu știe ce
  // drepturi ai arată mai puțin, nu mai mult.
  const [poateAdministra, setPoateAdministra] = useState(eu.rol === "ADMIN_TENANT");

  const reincarca = useCallback(async () => {
    setIncarca(true);
    try {
      const s = await api.jurisdictie();
      setStare(s);
      setForm({
        moneda: s.firma.moneda ?? "",
        cotaTVAImplicita: s.firma.cotaTVAImplicita == null ? "" : String(s.firma.cotaTVAImplicita),
        fusOrar: s.firma.fusOrar ?? "",
        taraStabilire: s.firma.taraStabilire ?? "",
      });
      setEroare("");
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setIncarca(false);
    }
  }, []);

  useEffect(() => { reincarca(); }, [reincarca]);
  useEffect(() => {
    api.drepturileMele()
      .then((d) => setPoateAdministra((d?.jurisdictie ?? []).includes("administrare")))
      .catch(() => {});
  }, []);

  async function fa(cheie, actiune) {
    setLucreaza(cheie); setEroare(""); setMesaj("");
    try {
      const r = await actiune();
      if (r?.motiv) setMesaj(r.motiv);
      if (r?.atentie) setMesaj(r.atentie);
      await reincarca();
    } catch (e) {
      setEroare(mesajEroare(e));
    } finally {
      setLucreaza("");
    }
  }

  /*
   * Toate cele douăzeci și cinci de secțiuni se deschid într-un `.continut`, cu
   * titlu. Asta era singura fără niciunul: omul ateriza pe un ecran care nu-i
   * spunea unde e, iar aranjarea în pagină ieșea din tiparul restului
   * aplicației. Inclusiv stările de încărcare și de eroare — altfel un refuz s-ar
   * afișa lipit de bara de sus.
   */
  if (incarca) {
    return (
      <div className="continut">
        <h2>{tr("Jurisdicție")}</h2>
        <div className="gol">{tr("Se încarcă…")}</div>
      </div>
    );
  }
  if (!stare) {
    return (
      <div className="continut">
        <h2>{tr("Jurisdicție")}</h2>
        <div className="eroare">{eroare || tr("Nu s-a putut citi jurisdicția.")}</div>
      </div>
    );
  }

  // Pachetul FIRMEI, nu „primul activ": cu RO si HU instalate amandoua,
  // rezumatul de mai jos trebuie sa fie al tarii ei.
  const activ = stare.pachete.find((p) => p.activ && p.cod === stare.firma.taraStabilire);
  const seAplica = stare.seAplica;

  return (
    <div className="continut">
      <h2>{tr("Jurisdicție")}</h2>
      <p className="subtitlu">
        {tr("Moneda, cota de TVA, fusul orar și zilele nelucrătoare după care se calculează termenele și se scriu cifrele. Se iau din pachetul țării obiectivului; unde nu e instalat niciunul, din alegerile firmei.")}
      </p>
      {eroare && <div className="eroare">{eroare}</div>}
      {mesaj && <div className="mesaj">{mesaj}</div>}

      <div className="panou">
        <h3>{tr("Ce se aplică")}</h3>
        <p className="subtitlu">
          {tr("Ordinea e fixă: pachetul țării")} <strong>{tr("obiectivului")}</strong> {tr("decide întâi; dacă nu e instalat niciunul, se folosesc alegerile firmei. Pot fi active mai multe pachete deodată — fiecare obiectiv îl ia pe al țării lui.")}
        </p>
        <p style={{ fontSize: "1.05rem" }}>
          {tr("Pentru afacerile firmei (")}{stare.firma.taraStabilire ?? tr("țară nedeclarată")}):{" "}
          <strong>{tr(NUME_SURSA[seAplica.sursa])}</strong>
          {seAplica.codTara ? tr(" — {0} ({1})", tr(seAplica.denumire), seAplica.codTara) : ""}
        </p>
        {stare.tariAcoperite?.length > 0 && (
          <p className="subtitlu">
            {tr("Obiective acoperite de un pachet:")} {stare.tariAcoperite.join(", ")}{tr(". Cele din alte țări folosesc alegerile firmei.")}
          </p>
        )}
        {seAplica.sursa === "nimic" && (
          <div className="eroare">
            {tr("Nu e configurat: lipsesc")} {seAplica.lipsa.join(", ")}{tr(". Până le completezi, devizele, ofertele și predarea către facturare vor refuza — deliberat, ca să nu iasă cifre într-o monedă presupusă.")}
          </div>
        )}
        {activ && (
          <p className="subtitlu">
            {activ.profilFiscal?.moneda} {tr("· cotă standard")} {activ.profilFiscal?.cotaStandard}%
            {activ.profilFiscal?.coteReduse?.length
              ? ` (reduse: ${activ.profilFiscal.coteReduse.join("%, ")}%)` : ""}
            {" · "}{activ.calendar?.fusOrar}
            {activ.calendar?.sarbatori?.length
              // ZILE distincte, nu intrări: două sărbători pot cădea în aceeași
              // zi (în 2026, a doua zi de Rusalii pică pe 1 iunie, peste Ziua
              // Copilului). Baza ține o zi o singură dată, iar motorul de
              // termene de acolo citește — deci numărul de pe ecran trebuie să
              // fie tot al zilelor, altfel spune cu unu mai mult decât e.
              ? tr(" · {0} zile de sărbătoare legală", new Set(activ.calendar.sarbatori.map((x) => x.data)).size)
              : ""}
          </p>
        )}
      </div>

      <div className="panou">
        <h3>{tr("Pachete de țară")}</h3>
        <p className="subtitlu">
          {tr("Un pachet e un fișier de")} <strong>{tr("date")}</strong>{tr(", nu cod: calendar, profil fiscal, profil de facturare, retenție, politică de semnătură, formate. O țară nouă nu cere nicio modificare de program.")}
        </p>
        {stare.pachete.length === 0 ? (
          <div className="gol">{tr("Niciun pachet instalat.")}</div>
        ) : (
          <div className="tabel-derulabil">
            <table>
              <thead>
                <tr>
                  <th>{tr("Țară")}</th><th>{tr("Versiune")}</th><th>{tr("Stare")}</th>
                  <th>{tr("Instalat")}</th><th>{tr("Sursă")}</th><th>{tr("Amprentă")}</th><th></th>
                </tr>
              </thead>
              <tbody>
                {stare.pachete.map((p) => (
                  <tr key={p.id}>
                    <td>{tr(p.denumire)} <span className="cod-mic">{p.cod}</span></td>
                    <td>{p.versiune}</td>
                    <td>{p.activ ? tr("activ") : tr("dezactivat")}</td>
                    <td>{data(p.importatLa)}</td>
                    <td>{p.semnatDe ?? "—"}</td>
                    <td><span className="cod-mic">{p.amprentaScurta}</span></td>
                    <td>
                      {poateAdministra && p.activ && (
                        <button
                          className="buton buton-mic buton-sters"
                          disabled={lucreaza === p.id}
                          onClick={() => fa(p.id, () => api.jurisdictieDezactiveaza(p.id))}
                        >
                          {tr("dezactivează")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {poateAdministra && (
          <div className="linie-actiune">
            <button
              className="buton buton-mic"
              disabled={lucreaza === "livrat"}
              onClick={() => fa("livrat", () => api.jurisdictieInstaleazaLivrat())}
            >
              {tr("instalează pachetul livrat (")}{tr(stare.pachetLivrat.denumire)} {stare.pachetLivrat.versiune})
            </button>

            {/*
              Importul unui pachet pentru altă țară. Fișierul se citește LOCAL,
              în browser, și se trimite ca JSON — nu ca încărcare de fișier:
              pachetul e date, nu un document de păstrat, iar semnătura lui se
              verifică pe server oricum.
            */}
            {stare.importDisponibil ? (
              <label className="buton buton-mic buton-sters" style={{ cursor: "pointer" }}>
                {tr("importă un pachet semnat…")}
                <input
                  type="file" accept="application/json,.json" style={{ display: "none" }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    e.target.value = "";
                    if (!f) return;
                    let plic;
                    try {
                      plic = JSON.parse(await f.text());
                    } catch {
                      setEroare(tr("Fișierul nu e un JSON valid."));
                      return;
                    }
                    await fa("import", () => api.jurisdictieImporta(plic));
                  }}
                />
              </label>
            ) : (
              <span className="subtitlu">
                {tr("Importul e indisponibil: nu e configurată nicio cheie publică de verificare, deci n-aș putea verifica semnătura niciunui fișier.")}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="panou">
        <h3>{tr("Alegerile firmei")}</h3>
        <p className="subtitlu">
          {tr("Se folosesc pentru obiectivele din țări fără pachet instalat. Rămân acolo și când există un pachet — el doar are întâietate.")}
        </p>
        <div className="linie-actiune">
          <label className="camp">
            <span>{tr("Monedă (ISO, 3 litere)")}</span>
            <input value={form.moneda} maxLength={3} disabled={!poateAdministra}
              onChange={(e) => setForm({ ...form, moneda: e.target.value })} />
          </label>
          <label className="camp">
            <span>{tr("Cotă TVA implicită (%)")}</span>
            <input type="number" step="0.1" min="0" max="100" value={form.cotaTVAImplicita}
              disabled={!poateAdministra}
              onChange={(e) => setForm({ ...form, cotaTVAImplicita: e.target.value })} />
          </label>
          <label className="camp">
            <span>{tr("Fus orar (ex. Europe/Bucharest)")}</span>
            <input value={form.fusOrar} disabled={!poateAdministra}
              onChange={(e) => setForm({ ...form, fusOrar: e.target.value })} />
          </label>
          <label className="camp">
            <span>{tr("Țara de stabilire")}</span>
            <input value={form.taraStabilire} maxLength={2} disabled={!poateAdministra}
              onChange={(e) => setForm({ ...form, taraStabilire: e.target.value })} />
          </label>
        </div>
        {poateAdministra && (
          <div className="linie-actiune">
            <button
              className="buton buton-mic"
              disabled={lucreaza === "alegeri"}
              onClick={() => fa("alegeri", () => api.jurisdictieAlege({
                ...(form.moneda ? { moneda: form.moneda } : {}),
                ...(form.cotaTVAImplicita !== "" ? { cotaTVAImplicita: Number(form.cotaTVAImplicita) } : {}),
                ...(form.fusOrar ? { fusOrar: form.fusOrar } : {}),
                ...(form.taraStabilire ? { taraStabilire: form.taraStabilire } : {}),
              }))}
            >
              {tr("salvează alegerile")}
            </button>
          </div>
        )}
        {!poateAdministra && (
          <p className="subtitlu">
            {tr("Le poate schimba cine are dreptul de")} <strong>{tr("administrare")}</strong> {tr("pe modulul Jurisdicție.")}
          </p>
        )}
      </div>
    </div>
  );
}
